import {
  Badge,
  Card,
  DataTable,
  Skeleton,
  StatusDot,
  Tabs,
  type Column,
  type StatusType,
} from '@zyxantech/tux'
import { useState } from 'react'
import { useGetAssetByIdQuery, type Asset } from '@/api/assets.api'
import { useGetGatewaysQuery, type Gateway } from '@/api/gateways.api'
import { useGetTagsQuery, type Tag } from '@/api/tags.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { PAGINATION } from '@/constants/pagination'
import { formatTimestamp } from '@/utils/format'

type TabId = 'overview' | 'gateways' | 'tags'

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'gateways', label: 'Gateways' },
  { id: 'tags', label: 'Tags' },
]

/** Render an arbitrary metadata value as a display string. */
function renderMeta(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

/** Map a gateway status string to the tux StatusDot status set. */
function toStatus(value: string): StatusType {
  if (value === 'online') return 'online'
  if (value === 'error') return 'error'
  return 'offline'
}

export function AssetDetailTabs({ assetId }: { assetId: string }) {
  const { data: asset, isLoading, error, refetch } = useGetAssetByIdQuery(assetId)
  const [tab, setTab] = useState<TabId>('overview')

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="40%" />
        <Skeleton height={120} />
      </div>
    )
  }
  if (error || !asset) {
    return (
      <div className="p-6">
        <ErrorState error={error} retry={() => void refetch()} />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border-subtle px-6 py-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold text-text-primary">{asset.name}</h2>
            <Badge variant="default">{asset.type}</Badge>
          </div>
          <div className="font-mono text-xs text-text-muted">{asset.path}</div>
        </div>
      </div>

      <div className="px-6 pt-3">
        <Tabs items={TABS} value={tab} onChange={(id) => setTab(id as TabId)} />
      </div>

      <div className="flex-1 overflow-auto p-6">
        {tab === 'overview' && <OverviewTab asset={asset} />}
        {tab === 'gateways' && <GatewaysTab assetId={assetId} />}
        {tab === 'tags' && <TagsTab assetId={assetId} />}
      </div>
    </div>
  )
}

function OverviewTab({ asset }: { asset: Asset }) {
  const fields: [string, string][] = [
    ['Asset ID', asset.id],
    ['Type', asset.type],
    ['Path', asset.path],
    ['Depth', String(asset.depth)],
    ['Created', formatTimestamp(asset.createdAt)],
    ['Updated', formatTimestamp(asset.updatedAt)],
  ]
  const metadata = asset.metadata ?? {}
  const metaEntries = Object.entries(metadata)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {fields.map(([label, value]) => (
          <Card key={label} padding="sm">
            <div className="text-xs text-text-muted">{label}</div>
            <div className="mt-0.5 truncate font-mono text-sm text-text-primary">{value}</div>
          </Card>
        ))}
      </div>

      {metaEntries.length > 0 && (
        <div>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            Metadata
          </h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {metaEntries.map(([key, value]) => (
              <Card key={key} padding="sm">
                <div className="text-xs text-text-muted">{key}</div>
                <div className="mt-0.5 truncate font-mono text-sm text-text-primary">
                  {renderMeta(value)}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function GatewaysTab({ assetId }: { assetId: string }) {
  const { data, isLoading, error, refetch } = useGetGatewaysQuery({
    assetId,
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  if (error) return <ErrorState error={error} retry={() => void refetch()} />

  const columns: Column<Gateway>[] = [
    { key: 'name', header: 'Name', accessor: (g) => g.name, sortable: true },
    {
      key: 'status',
      header: 'Status',
      accessor: (g) => (
        <span className="flex items-center gap-1.5">
          <StatusDot status={toStatus(g.status)} pulse={g.status === 'online'} size="sm" />
          {g.status}
        </span>
      ),
    },
    {
      key: 'lastSeenAt',
      header: 'Last seen',
      accessor: (g) => <span className="font-mono text-xs">{formatTimestamp(g.lastSeenAt)}</span>,
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={data?.items ?? []}
      loading={isLoading}
      rowKey={(g) => g.id}
      emptyMessage="No gateways attached to this asset."
    />
  )
}

function TagsTab({ assetId }: { assetId: string }) {
  const { data, isLoading, error, refetch } = useGetTagsQuery({
    assetId,
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  if (error) return <ErrorState error={error} retry={() => void refetch()} />

  const columns: Column<Tag>[] = [
    { key: 'name', header: 'Name', accessor: (t) => t.name, sortable: true },
    {
      key: 'address',
      header: 'Address',
      accessor: (t) => <span className="font-mono text-xs">{t.address}</span>,
    },
    { key: 'dataType', header: 'Type', accessor: (t) => t.dataType },
    { key: 'unit', header: 'Unit', accessor: (t) => t.unit ?? '—' },
    {
      key: 'enabled',
      header: 'Enabled',
      accessor: (t) => (
        <Badge variant={t.enabled ? 'success' : 'default'}>{t.enabled ? 'Yes' : 'No'}</Badge>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={data?.items ?? []}
      loading={isLoading}
      rowKey={(t) => t.id}
      emptyMessage="No tags attached to this asset."
    />
  )
}
